import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import kebabCase from 'lodash/kebabCase';

import CreatorInfoWithChannels from 'components/creator-info-with-channels/CreatorInfoWithChannels';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Spinner from 'components/spinner/Spinner';
import Overlay from 'components/overlay/Overlay';

import SOVKpiSelector from 'modules/insights/sov/components/analysis/kpis/SOVKpiSelector';
import SOVAnalysisResultsSection from 'modules/insights/sov/components/analysis/results/section/SOVAnalysisResultsSection';

import {
  fetchTopInfluencers,
  resetTopInfluencers,
  sortTopInfluencers
} from 'modules/insights/sov/reducers/top-creators/collabary/sov-top-influencers';

import { __t, applyTranslations } from 'utils/language';
import { abbrNum } from 'utils/helper/common';
import {
  TOP_INFLUENCERS_KPI_OPTIONS_INSTAGRAM,
  TOP_INFLUENCERS_KPI_OPTIONS_YOUTUBE,
  getReplacementValue
} from 'modules/insights/sov/utils/sov';

import './sov-top-influencers.scss';

import type {
  Channel,
  SOVReportType,
  SOVTopInfluencerData,
  SOVTopInfluencers as SOVTopInfluencersResults
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));
applyTranslations(require('./sov-top-influencers.i18n.json'));

type OwnProps = {
  reportType: SOVReportType,
  isFreemium: boolean
};

type StateProps = {
  topInfluencers: Array<SOVTopInfluencersResults>,
  isLoading: boolean,
  isSorting: boolean,
  sort: string
};

type DispatchProps = {
  dispatchResetTopInfluencers: () => void,
  dispatchFetchTopInfluencers: (a: SOVReportType) => void,
  dispatchSortTopInfluencers: (a: string) => void
};

type Props = OwnProps & StateProps & DispatchProps;

const COMMON_MSG_KEY = 'collabary_common';
const SHARED_MSG_KEY = 'sov_shared';
const MSG_KEY = 'sov_top_influencers';

export class SOVTopInfluencers extends React.PureComponent<Props> {
  componentDidUpdate({
    sort: prevSort
  }: Props) {
    const { sort, reportType, dispatchFetchTopInfluencers } = this.props;

    if (prevSort !== sort) {
      dispatchFetchTopInfluencers(reportType);
    }
  }

  componentWillUnmount(): void {
    this.props.dispatchResetTopInfluencers();
  }


  getEngagementRateValue(engagements?: number | null): string | undefined | null {
    return engagements ? engagements.toFixed(1) : null;
  }

  getYoutubePropValue(influencerChannel: Channel): number {
    const { sort } = this.props;

    return influencerChannel[sort];
  }

  getCreatorProfile = (influencerChannel: Channel) => {
    const { reportType } = this.props;
    const url = influencerChannel.url || '#';

    return {
      name: influencerChannel.username as string | undefined,
      profileImage: {
        url: influencerChannel.profilePicture || ''
      },
      channels: {
        [reportType]: {
          url,
          type: reportType,
          engagement: influencerChannel.engagementRate,
          followers: influencerChannel.followers
        }
      }
    };
  };

  handleSelection = (
    value: string
  ): void => {
    this.props.dispatchSortTopInfluencers(value);
  };

  renderAudienceValue(influencerChannel: Channel): React.ReactNode | undefined | null {
    const { reportType } = this.props;
    const dataProp = reportType === 'instagram' ? 'followers' : 'subscribers';
    const audienceTypeLabel = __t(`${COMMON_MSG_KEY}.kpis.${dataProp}`) || '';
    const value = abbrNum(influencerChannel[dataProp], { decimal: 1 });

    return influencerChannel[dataProp] && (
      <div
        className="sov-top-influencers__metric"
        title={`${audienceTypeLabel}: ${value}`}
      >
        <div className="sov-top-influencers__icon">
          <SVGDynamicIcon
            name={`channel-${reportType}-custom-fg`}
            customColor="grey"
            width={12}
            height={12}
          />
        </div>
        <div className="sov-top-influencers__audience-type-value">
          {value}
        </div>
      </div>
    );
  }

  renderNumPosts(influencerChannel: Channel, orderByFieldCount: number): React.ReactNode {
    const { sort, reportType } = this.props;
    const value = isNil(orderByFieldCount) ? 0 : abbrNum(orderByFieldCount);
    let specificKey = sort;

    if (sort === 'posts') {
      specificKey = reportType === 'instagram' ? specificKey : 'videos_posted';
    }

    const msgKey = (__t(`${COMMON_MSG_KEY}.kpis.${specificKey}`) || '').replace(/\sposted/, '');

    return (
      <div
        className="sov-top-influencers__selected-metric-count-container"
        title={`${value} ${msgKey}`}
      >
        <div className="sov-top-influencers__selected-metric-count">
          {value}
        </div>
        <div className="sov-top-influencers__selected-metric-label">
          {msgKey.toLocaleLowerCase()}
        </div>
      </div>
    );
  }

  renderInfluencerDetails = ({
    channel: influencerChannel,
    orderByFieldCount
  }: SOVTopInfluencerData) => {
    const { isFreemium } = this.props;
    const key = kebabCase(`sov-top-influencers__influencer--${String(influencerChannel.username || influencerChannel.channelId)}`);

    return (
      <div
        key={key}
        id={key}
        className="sov-top-influencers__influencer"
      >
        <div className="sov-top-influencers__entity">
          <CreatorInfoWithChannels
            creatorProfile={this.getCreatorProfile(influencerChannel)}
          />
        </div>
        <div className="sov-top-influencers__selected-metric">
          {this.renderNumPosts(influencerChannel, orderByFieldCount)}
        </div>
        {
          isFreemium && (
            <Overlay
              parentId={key}
              text={__t(`${SHARED_MSG_KEY}.messages.available_in_paid_version`)}
              className="sov-top-influencers__overlay"
              showOnHover
            />
          )
        }
      </div>
    );
  };

  renderInfluencers(): React.ReactNode {
    const { topInfluencers } = this.props;

    return (
      <div className="sov-top-influencers__influencers">
        {
          topInfluencers.map((item, index) => (
            <div
              key={`sov-top-influencers__influencers-row--${item.searchTerm}-${index}`}
              className="sov-top-influencers__influencers-row"
            >
              {item.influencers.map(this.renderInfluencerDetails)}
            </div>
          ))
        }
      </div>
    );
  }

  renderQueries(): React.ReactNode {
    const { topInfluencers } = this.props;

    return (
      <div className="sov-top-influencers__terms">
        {
          topInfluencers.map((item, index) => (
            <div
              key={`sov-top-influencers__term--${item.searchTerm}-${index}`}
              className="sov-top-influencers__term text-overflow-ellipsis"
            >
              {getReplacementValue(item.searchTerm)}
            </div>
          ))
        }
      </div>
    );
  }

  renderHeader(): React.ReactNode {
    const { sort, reportType } = this.props;

    return (
      <div className="sov-top-influencers__header">
        <div className="sov-top-influencers__header-terms">
          {__t(`${SHARED_MSG_KEY}.labels.keyword`)}
        </div>
        <div className="sov-top-influencers__header-influencers">
          {__t(`${MSG_KEY}.${reportType}.description.${sort}`)}
        </div>
      </div>
    );
  }

  renderContent() {
    return (
      <div className="sov-top-influencers">
        {this.renderHeader()}
        <div className="sov-top-influencers__content">
          {this.renderQueries()}
          {this.renderInfluencers()}
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    const { topInfluencers, reportType, isLoading, sort, isSorting } = this.props;

    const dataColumns = reportType === 'instagram'
      ? TOP_INFLUENCERS_KPI_OPTIONS_INSTAGRAM
      : TOP_INFLUENCERS_KPI_OPTIONS_YOUTUBE;

    const kpiOptions = Object.keys(dataColumns).map(value => ({
      label: __t(`${COMMON_MSG_KEY}.kpis.${dataColumns[value]}`),
      value
    }));

    return (
      <SOVAnalysisResultsSection
        isEmpty={isEmpty(topInfluencers)}
        isLoading={isLoading}
        header={__t(`${MSG_KEY}.all.label_section_header`)}
      >
        <div className="sov-top-influencers-container">
          <div className="sov-top-influencers-container__header">
            <SOVKpiSelector
              label={__t(`${SHARED_MSG_KEY}.labels.select_kpi`)}
              options={kpiOptions}
              value={sort}
              onChange={this.handleSelection}
            />
          </div>
          {isSorting ? (
            <div className="sov-analysis-top-influencers__spinner">
              <Spinner
                position="inline"
                size="small"
                color="light"
              />
            </div>
          ) : this.renderContent()}
        </div>
      </SOVAnalysisResultsSection>
    );
  }
}

const mapStateToProps = ({ sovTopInfluencers: { topInfluencers, isLoading, isSorting, sort } }): StateProps => ({
  topInfluencers,
  isLoading,
  isSorting,
  sort
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchResetTopInfluencers: () => dispatch(resetTopInfluencers()),
  dispatchSortTopInfluencers: (sort) => dispatch(sortTopInfluencers(sort)),

  dispatchFetchTopInfluencers: (reportType: SOVReportType) => (
    dispatch(fetchTopInfluencers(true, reportType))
  )
});


export default connect(mapStateToProps, mapDispatchToProps)(SOVTopInfluencers);
